import { useMemo } from 'react';
import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import type { ChartSeries, ChartSeriesToken } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { useLegendToggle } from '../../../molecules/ChartLegend/useLegendToggle';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { token } from '../../../tokens/tokens';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type ShareOfPositiveView = {
  /** `Your share of positive`, `Rank`, `Leader`. */
  kpis: KPIMetric[];
  /** One row per brand, highest positive share first; one key, `positiveShare`. */
  rows: ChartRow[];
  /** The brand option value behind each row, for the drill-down. */
  keys: string[];
  xKey: string;
  series: ChartSeries[];
  /** Each bar's brand colour, in bar order — the legend's too. */
  bars: { label: string; color: ChartSeriesToken }[];
  /**
   * The positive share across every mention in scope — the dashed reference
   * line. None where the bars have no market figure to set them against (the
   * executive `Official posts by brand`).
   */
  threshold?: number;
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type ShareOfPositiveCardProps = WidgetBlockProps & {
  view?: ShareOfPositiveView;
  /** Opens the conversations behind a bar (index into `rows`). */
  onViewDetails?: (index: number) => void;
  /**
   * The executive dashboard draws a client's sister brands here too, which are
   * not competitors, so it names the card by brand instead. Default: Figma's.
   */
  title?: string;
  /**
   * It also draws another figure against the market's on these bars
   * (`Engagement per mention by brand`), which its help then explains. Default: Figma's.
   */
  helpText?: string;
  helpSupportingText?: string;
  /**
   * Each legend entry shows or hides its brand (the executive view, GP
   * 2026-11-03). Only what is drawn changes: the figures above stay of every
   * brand. Default off.
   */
  toggleable?: boolean;
};

/**
 * Figma `931:36991` — each brand's favourable share against the category mean.
 *
 * Category-scoped: the `Brands` chip does the scoping, so the user can deselect
 * us and "your share of positive" then reads an em dash. The category mean is
 * the share over **every** mention, not the mean of the bars — mention-weighted,
 * so a brand with forty mentions cannot pull the benchmark as hard as one with
 * four thousand.
 */
export function ShareOfPositiveCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
  title = 'Share of positive vs competitors',
  helpText = 'Compares the percentage of each brand’s mentions that are positive. The dashed line shows the overall positive percentage across all mentions of the selected brands.',
  helpSupportingText,
  toggleable = false,
}: ShareOfPositiveCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  const keys = useMemo(() => view?.keys ?? [], [view]);
  const legend = useLegendToggle(keys);
  /* The bars drawn, as indexes into the view's; every one unless the legend hid some. The market line stays. */
  const drawn = toggleable ? legend.shown : keys.map((_, i) => i);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title={title}
      helpText={helpText}
      helpSupportingText={helpSupportingText}
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={drawn.map((i) => view.rows[i]!)}
            xKey={view.xKey}
            onViewDetails={onViewDetails && ((index) => onViewDetails(drawn[index]!))}
            series={view.series}
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            threshold={view.threshold}
            /* Each bar in its own brand's colour, so a brand reads the same here
               as it does in the donut above and the trend below. */
            cellColors={drawn.map((i) => view.bars[i]!.color)}
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      /* Keyed by brand, in bar order, so each colour reads the same as the donut's. */
      legend={
        view && (
          <ChartLegend
            items={view.bars.map((bar, i) => ({
              label: bar.label,
              color: token[bar.color],
              off: toggleable && legend.isOff(keys[i]!),
            }))}
            onToggle={toggleable ? (i) => legend.toggle(keys[i]!) : undefined}
          />
        )
      }
    />
  );
}
